import { handleActions, ReducerMap } from "redux-actions";
import {
    fetchActions,
    initialBaseState,
    IFetchStoreState,
    progressActions,
    updateActions,
} from "reducers/base-reducers";
import { CUSTOMIZATION } from "constants/action-types";
import {
    ICustomizationTemplate,
    IProductDataEntry,
    IImageUrlData,
    IUploadPayload,
    ICheckCustomizationPayload,
} from "constants/product-customizations/customization";
import { TCustomizationSelection } from "constants/product-customizations/customization-selection.types";
import { combineReducers } from "redux";
import { format } from "lib/globalize-util";
import { ICustomizationImageEntity } from "epics/customizations-epic";
import { IProduct } from "../records/item/item";
import { ICompany } from "components/contacts/contacts-types";
import { IProductCustomizationImageType, IProductCustomizationPrint } from "../records/brand/brand";
import { ICustomizationImageTagSchema } from "../constants/product-customizations/customization-image-tag-schema";
import { IOrder } from "../records/order";

export interface ISelectedProductState {
    product: IProduct;
    isFetching: boolean;
}

export interface IEditItemPayload {
    itemId: string;
    customizations: TCustomizationSelection[];
    orderId?: string;
    templateId?: string;
    productName?: string;
    companyId?: string;
    companyMerchantId?: string;
    proofId?: string;
}

export interface ICustomizationState {
    company?: IFetchStoreState<ICompany>;
    current: IFetchStoreState<ICustomizationTemplate>;
    selection: {
        customizations: TCustomizationSelection[];
        templateId: string;
    };
    editItem: IEditItemPayload;
    proofWarning: IEditItemPayload;
    imageUpload: any;
    existingImages: {
        isFetching: boolean;
        isSearching: boolean;
        error?: string | Error;
        payload: { images: Array<ICustomizationImageEntity | null>; nextPageToken: string };
    };
    selectedProduct: ISelectedProductState;
    bulkCustomization: {
        product: IProduct;
        productDataEntries: IProductDataEntry[];
        customizations: TCustomizationSelection[];
        templateId: string;
        payload: IProduct[];
        isFetchingProducts: boolean;
    };
    orderCustomizations: {
        products: IProduct[];
        templates: ICustomizationTemplate[];
        isFetching: boolean;
    };
    checkedImages: {
        isChecking: boolean;
        verifiedImages: ICustomizationImageEntity[] | null;
        error?: string | Error;
    };
    imagesTab: {
        isChecking: boolean;
        verifiedImages: ICustomizationImageEntity[] | null;
        error?: string | Error;
    };
    brandSettings: {
        isUpdating: boolean;
        printTypes: IProductCustomizationPrint[];
        imageTypes: IProductCustomizationImageType[];
        error: string | Error | null;
    };
    order: {
        isChecking: false;
        validationPayload: null | ICheckCustomizationPayload;
        checkError?: string | Error | null;
    };
    imageTagSchema: {
        brandSchemas: ICustomizationImageTagSchema[];
        isBrandSchemasFetching: boolean;
        brandSchemasError: string | Error | null;
    };
    logosManagement: {
        isFetching: boolean;
        isSearching: boolean;
        error?: string | Error;
        logoIsUpdating: boolean;
        logoUpdateError: string | Error;
        payload: { logos: Array<ICustomizationImageEntity | null>; nextPageToken: string };
    };
    imageURLs: {
        fetchingURLs: string[];
        payload: {
            urls: IImageUrlData[];
        };
        isFetching: boolean;
    };
    proofCreate: {
        payload: IUploadPayload;
        orderId: string;
        file: string;
        isCreating: boolean;
        lineItemId: string;
        error: string | Error | null;
    };
    proofUpload: {
        isUploading: boolean;
        error: string | Error | null;
    };
    proofAssign: {
        updatedOrder: IOrder;
        isAssigning: boolean;
        error: string | Error | null;
    };
}

const initialProofCreateState = {
    payload: null,
    orderId: "",
    file: null,
    isCreating: false,
    error: null,
};

const initialProofUploadState = {
    payload: null,
    isUploading: false,
    error: null,
};

const initialProofAssignState = {
    updatedOrder: null,
    isAssigning: false,
    error: null,
};

const initialImageUploadState = {
    isUploading: false,
    payload: null,
    error: null,
};

const initialOrderState: ICustomizationState["order"] = {
    isChecking: false,
    checkError: null,
    validationPayload: null,
};

const initialImageUrlState = {
    error: null,
    isFetching: false,
    fetchingURLs: [],
    payload: {
        urls: [],
    },
};

interface IProductPayload {
    product: IProduct | null;
}

export const initialBulkCustomizationState = {
    product: null,
    productDataEntries: [],
    customizations: [],
    payload: [],
    templateId: "",
    isFetchingProducts: false,
};

export const checkedImagesInitialState = {
    verifiedImages: null,
    isChecking: false,
    error: null,
};

export const checkedTabInitialState = {
    verifiedImages: null,
    isChecking: false,
    error: null,
};

export const initialSelectedProductState = {
    product: null,
    isFetching: false,
};

export const initialLogosManagementState = {
    payload: {
        logos: [],
        nextPageToken: null,
    },
    isFetching: false,
    isSearching: false,
    logoIsUpdating: false,
    logoUpdateError: null,
};

export const initialImageTagSchemasState = {
    brandSchemas: null,
    isBrandSchemasFetching: false,
    brandSchemasError: null,
};

interface IBulkCustomizationModalPayload {
    product: IProduct;
    productDataEntries: IProductDataEntry[];
    customizations: TCustomizationSelection[];
    templateId: string;
}

export const customization = combineReducers<ICustomizationState>({
    current: handleActions<IFetchStoreState<ICustomizationTemplate>>(
        {
            ...fetchActions(CUSTOMIZATION.PRODUCT_TEMPLATES.FETCH),
        } as any,
        initialBaseState,
    ),

    selection: handleActions(
        {
            [CUSTOMIZATION.SET_SELECTION]: (
                state,
                { payload }: { payload: { customizations: TCustomizationSelection[]; templateId: string } },
            ) => ({
                ...state,
                ...payload,
            }),
        } as any,
        {},
    ),
    bulkCustomization: handleActions(
        {
            ...progressActions(CUSTOMIZATION.BULK_CUSTOMIZATIONS.PRODUCTS.FETCH, "isFetchingProducts"),
            [CUSTOMIZATION.BULK_CUSTOMIZATIONS.RESET]: (state) => ({
                ...initialBulkCustomizationState,
            }),
            [CUSTOMIZATION.BULK_CUSTOMIZATIONS.PRODUCTS.CLEAR]: (state) => ({
                ...state,
                payload: [],
            }),
            [CUSTOMIZATION.BULK_CUSTOMIZATIONS_MODAL]: (
                state,
                {
                    payload,
                }: {
                    payload: IBulkCustomizationModalPayload;
                },
            ) => ({
                ...state,
                ...payload,
            }),
        } as any,
        initialBulkCustomizationState,
    ),
    editItem: handleActions(
        {
            [CUSTOMIZATION.EDIT_LINE_ITEM]: (
                state,
                { payload }: { payload: IEditItemPayload } = {
                    payload: { itemId: "", customizations: [] },
                },
            ) => {
                return {
                    ...state,
                    ...payload,
                };
            },
        } as any,
        {},
    ),
    proofWarning: handleActions(
        {
            [CUSTOMIZATION.PROOF_WARNING_MODAL]: (
                state,
                { payload }: { payload: IEditItemPayload } = {
                    payload: { itemId: "", customizations: [] },
                },
            ) => {
                return {
                    ...state,
                    ...payload,
                };
            },
        } as any,
        {},
    ),
    imageUpload: handleActions(
        {
            ...progressActions(CUSTOMIZATION.CREATE_CUSTOMIZATION_IMAGE.CREATE, "isUploading"),
            [CUSTOMIZATION.CREATE_CUSTOMIZATION_IMAGE.CLEAR]: (state) => {
                return {
                    ...state,
                    ...initialImageUploadState,
                };
            },
        } as any,
        { isUploading: false, payload: null },
    ),
    imageURLs: handleActions(
        {
            ...progressActions(CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.FETCH, "isFetching"),
            [CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.FETCH.START]: (state: any, action: any) => {
                const imageId = action.payload.imageId;
                const fetchingURLs = state.fetchingURLs;
                fetchingURLs.push(imageId);
                return {
                    ...state,
                    fetchingURLs: fetchingURLs,
                    isFetching: true,
                };
            },
            [CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.FETCH.SUCCESS]: (state: any, action: any) => {
                const urls = action.payload.urlsList.map((u) => {
                    return { ...u, imageId: action.payload.imageId };
                });

                return {
                    ...state,
                    payload: {
                        urls: state.payload.urls.concat(urls),
                    },
                    isFetching: false,
                };
            },
            [CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.RESET]: () => {
                return initialImageUrlState;
            },
        } as any,
        initialImageUrlState,
    ),
    selectedProduct: handleActions(
        {
            [CUSTOMIZATION.PRODUCT.FETCH.START]: (state: any) => {
                return {
                    ...state,
                    product: null,
                    isFetching: true,
                };
            },
            [CUSTOMIZATION.PRODUCT.RESET]: (state: any) => {
                return initialSelectedProductState;
            },
            [CUSTOMIZATION.PRODUCT.FETCH.SUCCESS]: (state: any, { payload }) => {
                return {
                    ...state,
                    product: payload?.product,
                    isFetching: false,
                };
            },
        } as ReducerMap<ISelectedProductState, IProductPayload>,
        initialSelectedProductState as ISelectedProductState,
    ),
    order: handleActions(
        {
            [CUSTOMIZATION.ORDER.CHECK.START]: (state: any) => {
                return {
                    ...state,
                    isChecking: true,
                };
            },
            [CUSTOMIZATION.ORDER.CHECK.SUCCESS]: (state: any, { payload }) => {
                return {
                    ...state,
                    validationPayload: payload,
                    isChecking: false,
                };
            },
            [CUSTOMIZATION.ORDER.CHECK.ERROR]: (state: any, { payload }) => {
                return {
                    ...state,
                    checkError: payload,
                    validationPayload: null,
                    isChecking: false,
                };
            },
            [CUSTOMIZATION.ORDER.RESET]: () => {
                return initialOrderState;
            },
        },
        initialOrderState,
    ),
    existingImages: handleActions(
        {
            ...updateActions(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES),
            // TODO: Fix problem. Sometimes data is set to another state: {notifications.feeds}
            ...progressActions(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH, "isFetching"),
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH.SUCCESS]: (state: any, action: any) => {
                const { dataList: images, nextPageToken } = action.payload;
                const { payload, isSearching } = state;
                const stateImages = payload.images;

                if (!Array.isArray(images)) {
                    return {
                        isFetching: false,
                        isSearching: false,
                        error: format("Received a malformed response while fetching product customization images"),
                    };
                }

                if (!stateImages.length && images?.length === 0 && isSearching) {
                    return {
                        payload: {
                            images,
                            nextSearchPageToken: null,
                        },
                        isSearching,
                        isFetching: false,
                        error: format("No results"),
                    };
                }

                // adding null in the end lets components know when there is more to load from the server
                if (nextPageToken) {
                    images.push(null);
                }

                const allImages = stateImages.slice(0, -1).concat(images);

                return {
                    payload: {
                        images: allImages,
                        nextPageToken,
                    },
                    isSearching,
                    isFetching: false,
                };
            },
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH.CANCEL]: (state: any) => {
                return {
                    ...state,
                    isFetching: false,
                };
            },
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET]: (state: any) => {
                const { isSearching } = state;
                return {
                    isFetching: false,
                    isSearching,
                    error: null,
                    payload: {
                        images: [],
                        nextPageToken: null,
                    },
                };
            },
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.SET_STORED_VALUES]: (state: any, action: any) => {
                return {
                    ...state,
                    payload: action.payload,
                };
            },
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.SEARCH]: (state: any, action: any) => {
                const { isSearching } = action.payload;
                return {
                    ...state,
                    isSearching,
                };
            },
        } as any,
        {
            isFetching: false,
            isSearching: false,
            error: null,
            payload: {
                images: [],
                nextPageToken: null,
            },
        },
    ),
    company: handleActions<IFetchStoreState<ICompany>>(
        {
            ...fetchActions(CUSTOMIZATION.COMPANY.FETCH),
        } as any,
        initialBaseState,
    ),
    checkedImages: handleActions(
        {
            ...progressActions(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK, "isChecking"),
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET_CHECKED]: (state) => ({
                ...checkedImagesInitialState,
            }),
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK.SUCCESS]: (state: any, action: any) => {
                const { dataList: verifiedImages } = action.payload;

                if (!Array.isArray(verifiedImages)) {
                    return {
                        isChecking: false,
                        error: format("Received a malformed response while checking product customization images"),
                    };
                }
                return {
                    ...state,
                    verifiedImages,
                    isChecking: false,
                };
            },
        } as any,
        checkedImagesInitialState,
    ),
    imagesTab: handleActions(
        {
            ...progressActions(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK_IMAGE_TAB, "isChecking"),
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.RESET_IMAGE_TAB]: (state) => ({
                ...checkedTabInitialState,
            }),
            [CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK_IMAGE_TAB.SUCCESS]: (state: any, action: any) => {
                const { dataList: verifiedImages } = action.payload;

                if (!Array.isArray(verifiedImages)) {
                    return {
                        isChecking: false,
                        error: format("Received a malformed response while checking image tab"),
                    };
                }

                return {
                    ...state,
                    verifiedImages,
                    isChecking: false,
                };
            },
        } as any,
        checkedTabInitialState,
    ),
    brandSettings: handleActions(
        {
            [CUSTOMIZATION.BRAND_SETTINGS.UPDATE.START]: (state: any) => {
                return {
                    ...state,
                    isUpdating: true,
                };
            },
            [CUSTOMIZATION.BRAND_SETTINGS.UPDATE.SUCCESS]: (state: any, action: any) => {
                return {
                    ...state,
                    printTypes: action?.payload?.prints,
                    imageTypes: action?.payload?.image_types,
                    isUpdating: false,
                };
            },
            [CUSTOMIZATION.BRAND_SETTINGS.UPDATE.ERROR]: (state: any, action: any) => {
                return {
                    ...state,
                    isUpdating: false,
                    error: action?.error?.message,
                };
            },
            [CUSTOMIZATION.BRAND_SETTINGS.RESET_ERROR]: (state: any) => {
                return {
                    ...state,
                    error: null,
                };
            },
        } as any,
        {
            isUpdating: false,
            printTypes: null,
            imageTypes: null,
            error: null,
        },
    ),
    logosManagement: handleActions(
        {
            ...progressActions(CUSTOMIZATION.LIST_MANAGE_LOGOS.FETCH, "isFetching"),
            [CUSTOMIZATION.LIST_MANAGE_LOGOS.FETCH.SUCCESS]: (state: any, action: any) => {
                const { dataList: logos, nextPageToken } = action.payload;
                const { payload, isSearching } = state;
                const stateLogos = payload.logos;

                if (!Array.isArray(logos)) {
                    return {
                        isFetching: false,
                        isSearching: false,
                        error: format("Received a malformed response while fetching product customization images"),
                    };
                }

                if (!stateLogos.length && logos?.length === 0 && isSearching) {
                    return {
                        payload: {
                            logos,
                            nextSearchPageToken: null,
                        },
                        isSearching,
                        isFetching: false,
                        logoIsUpdating: false,
                        logoUpdateError: false,
                        error: format("No results"),
                    };
                }

                // adding null in the end lets components know when there is more to load from the server
                if (nextPageToken) {
                    logos.push(null);
                }

                const allLogos = stateLogos.slice(0, -1).concat(logos);

                return {
                    payload: {
                        logos: allLogos,
                        nextPageToken,
                    },
                    isSearching,
                    isFetching: false,
                    logoIsUpdating: false,
                    logoUpdateError: false,
                };
            },
            [CUSTOMIZATION.LIST_MANAGE_LOGOS.FETCH.CANCEL]: (state: any) => {
                return {
                    ...state,
                    isFetching: false,
                };
            },
            [CUSTOMIZATION.LIST_MANAGE_LOGOS.RESET]: (state: any) => {
                const { isSearching } = state;
                return {
                    isFetching: false,
                    isSearching,
                    logoIsUpdating: false,
                    logoUpdateError: false,
                    error: null,
                    payload: {
                        logos: [],
                        nextPageToken: null,
                    },
                };
            },
            [CUSTOMIZATION.CUSTOMIZATION_IMAGE.UPDATE.START]: (state: any) => {
                return {
                    ...state,
                    logoIsUpdating: true,
                };
            },
            [CUSTOMIZATION.CUSTOMIZATION_IMAGE.UPDATE.SUCCESS]: (state: any, action: any) => {
                return {
                    ...state,
                    logoIsUpdating: false,
                };
            },
            [CUSTOMIZATION.CUSTOMIZATION_IMAGE.UPDATE.ERROR]: (state: any, action: any) => {
                return {
                    ...state,
                    logoIsUpdating: false,
                    logoUpdateError: action?.error?.message,
                };
            },
        } as any,
        initialLogosManagementState,
    ),
    imageTagSchema: handleActions(
        {
            [CUSTOMIZATION.IMAGE_TAG_SCHEMAS.BRAND.FETCH.START]: (state: any) => {
                return {
                    ...state,
                    isBrandSchemasFetching: true,
                };
            },
            [CUSTOMIZATION.IMAGE_TAG_SCHEMAS.BRAND.FETCH.SUCCESS]: (state: any, { payload }) => {
                return {
                    ...state,
                    brandSchemas: payload,
                    isBrandSchemasFetching: false,
                };
            },
            [CUSTOMIZATION.IMAGE_TAG_SCHEMAS.BRAND.FETCH.ERROR]: (state: any, { payload }) => {
                return {
                    ...state,
                    brandSchemas: null,
                    isBrandSchemasFetching: false,
                    brandSchemasError: payload,
                };
            },
        },
        initialImageTagSchemasState,
    ),
    proofCreate: handleActions(
        {
            [CUSTOMIZATION.PROOF.CREATE.START]: (state: any, { payload }) => {
                const { orderId, file, lineItemId } = payload;
                return {
                    ...state,
                    lineItemId,
                    orderId,
                    file,
                    isCreating: true,
                };
            },
            [CUSTOMIZATION.PROOF.CREATE.SUCCESS]: (state: any, { payload }) => {
                return {
                    ...state,
                    payload,
                    isCreating: false,
                };
            },
            [CUSTOMIZATION.PROOF.CREATE.ERROR]: (state: any, { payload }) => {
                return {
                    ...state,
                    payload: null,
                    isCreating: false,
                    error: payload,
                };
            },
            [CUSTOMIZATION.PROOF.RESET]: () => {
                return initialProofCreateState;
            },
        },
        initialProofCreateState,
    ),
    proofUpload: handleActions(
        {
            [CUSTOMIZATION.PROOF.UPLOAD.START]: (state: any) => {
                return {
                    ...state,
                    isUploading: true,
                };
            },
            [CUSTOMIZATION.PROOF.UPLOAD.SUCCESS]: (state: any) => {
                return {
                    ...state,
                    isUploading: false,
                };
            },
            [CUSTOMIZATION.PROOF.UPLOAD.ERROR]: (state: any, { payload }) => {
                return {
                    ...state,
                    isUploading: false,
                    error: payload,
                };
            },
            [CUSTOMIZATION.PROOF.RESET]: () => {
                return initialProofUploadState;
            },
        },
        initialProofUploadState,
    ),
    proofAssign: handleActions(
        {
            [CUSTOMIZATION.PROOF.ASSIGN.START]: (state: any) => {
                return {
                    ...state,
                    isAssigning: true,
                };
            },
            [CUSTOMIZATION.PROOF.ASSIGN.SUCCESS]: (state: any, { payload }) => {
                return {
                    ...state,
                    updatedOrder: payload,
                    isAssigning: false,
                };
            },
            [CUSTOMIZATION.PROOF.ASSIGN.ERROR]: (state: any, { payload }) => {
                return {
                    ...state,
                    updatedOrder: null,
                    isAssigning: false,
                    error: payload,
                };
            },
            [CUSTOMIZATION.PROOF.RESET]: () => {
                return initialProofAssignState;
            },
        },
        initialProofAssignState,
    ),
});
